import { ReactElement } from 'react';
import { isEmpty } from 'lodash';

import { LinkMapping, TrustSignalsBadgeContainer } from '@dbt-labs/metadata-shared';
import { CellContext, Icon, Link, RyeconEyeClosed, Tooltip } from '@dbt-labs/sourdough';

import { HealthIssueType, SourceHealthIssueType } from '@/__generated__/graphql';
import { ProjectScope } from '@/hooks/params';
import { UrlEnvironmentType } from '@/typings/url';
import {
  AccountParams,
  LinkGenerator,
  UniqueIdParams,
  UrlProjectParams,
} from '@/util/linkTypes';

interface LinkCellDisplayProps {
  value: any;
  href: string | undefined;
  isInternal?: boolean;
  rowIndex?: number;
  columnId?: string;
}

// TODO: Not all usages of this are internal links
const LinkCellDisplay = ({
  value,
  href,
  isInternal = true,
  rowIndex,
  columnId,
}: LinkCellDisplayProps) => {
  if (href) {
    return (
      <Tooltip
        displayOnlyWhenTruncated
        content={value}
        className="w-full"
        childrenAreInteractive={true}
      >
        {(ref) => (
          <Link
            to={href}
            isInternal={isInternal}
            testId={
              rowIndex !== undefined && columnId
                ? `link-${columnId}-${rowIndex}`
                : rowIndex !== undefined
                  ? `link-row-${rowIndex}`
                  : `link-${value}`
            }
          >
            <div ref={ref} className="cursor-pointer truncate underline">
              {value}
            </div>
          </Link>
        )}
      </Tooltip>
    );
  }
  return (
    <Tooltip
      displayOnlyWhenTruncated
      content={value}
      className="w-full"
      childrenAreInteractive={true}
    >
      {(ref) => (
        <div ref={ref} className="truncate underline">
          {value}
        </div>
      )}
    </Tooltip>
  );
};

type LinkCellProps<TData extends object> = CellContext<TData, ReactElement> & {
  href?: string;
  isInternal?: boolean;
  columnId?: string;
};

/**
 * A cell that is styled like a link
 * Requires onRowClickLinkGenerator in the table for correct link generation.
 */
export const LinkCell = <TData extends object>({
  getValue,
  href,
  isInternal,
  row,
  columnId,
}: LinkCellProps<TData>) => {
  const value = getValue();
  const rowIndex = row?.index;
  return (
    <LinkCellDisplay
      value={value}
      href={href}
      isInternal={isInternal}
      rowIndex={rowIndex}
      columnId={columnId}
    />
  );
};

type ResourceTableData = {
  uniqueId: string;
};

/**
 * A cell function that is styled like a link
 * Used when onRowClickLinkGenerator cannot be used, i.e. interactive table cells
 */
export const getNameLinkCell = (
  getDetailsPageLink: LinkGenerator<AccountParams & UrlProjectParams & UniqueIdParams>,
  projectScope: ProjectScope,
) => {
  return ({
    getValue,
    row: {
      original: { uniqueId },
      index,
    },
  }: CellContext<ResourceTableData, any>) => (
    <LinkCellDisplay
      value={getValue()}
      href={getDetailsPageLink({
        ...projectScope,
        dbtUniqueId: uniqueId,
      })}
      rowIndex={index}
      columnId="name"
    />
  );
};

/**
 * A cell function that combines a name link with a health issues badge
 * Used when you want to display both the name and health status in the same cell
 */
export const getNameWithHealthLinkCell = (
  getDetailsPageLink: LinkGenerator<AccountParams & UrlProjectParams & UniqueIdParams>,
  projectScope: ProjectScope,
  getLinkMapping: (params: {
    healthIssues: (HealthIssueType | SourceHealthIssueType)[];
    projectId: number;
    environmentType: UrlEnvironmentType;
    uniqueId: string;
  }) => LinkMapping,
) => {
  return ({
    getValue,
    row: {
      original: { uniqueId, healthIssues },
      index,
    },
  }: CellContext<
    ResourceTableData & {
      healthIssues: (HealthIssueType | SourceHealthIssueType)[];
      projectId: number;
    },
    any
  >) => {
    const nameValue = getValue();
    const linkMapping = getLinkMapping({
      healthIssues,
      projectId: projectScope.projectId,
      environmentType: projectScope.environmentType,
      uniqueId,
    });

    return (
      <div className="flex items-center gap-2">
        <TrustSignalsBadgeContainer
          resourceType="model"
          trustSignals={{
            healthIssues,
            additionalMetadata: !isEmpty(linkMapping) ? { linkMapping } : undefined,
          }}
        />
        <LinkCellDisplay
          value={nameValue}
          href={getDetailsPageLink({
            ...projectScope,
            dbtUniqueId: uniqueId,
          })}
          rowIndex={index}
          columnId="name"
        />
      </div>
    );
  };
};

/**
 * A cell function that combines a name with an access indicator
 */
export const getNameWithAccessLinkCell = (
  getLink: (row: { projectId: number; hasAccess: boolean }) => string | undefined,
) => {
  return ({
    getValue,
    row: { original, index },
  }: CellContext<
    {
      projectId: number;
      hasAccess: boolean;
    },
    any
  >) => {
    const value = getValue();
    const href = getLink(original);
    const { hasAccess } = original;

    return (
      <>
        <LinkCellDisplay value={value} href={href} rowIndex={index} columnId="name" />
        {!hasAccess && (
          <Tooltip
            className="inline-block text-base font-normal text-fgMain"
            content="You do not have access to this project"
          >
            <Icon
              testId="noAccessIcon"
              size="xs"
              className="ml-1 mt-1 align-text-top text-fgMain"
              ryecon={RyeconEyeClosed}
            />
          </Tooltip>
        )}
      </>
    );
  };
};
